const CONFIG_STORAGE_KEY = 'cms_event_dedup_config';
const AMPLITUDE_EVENT_CONFIG_URL =
  'https://cms.unacademy.com/amplitude-event-config';

const DEDUP_COOKIE_NAME = 'cms_event_dedup';
const DEDUP_COOKIE_DOMAIN = '.unacademy.com';
const DEDUP_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;
const DEDUP_ENTRY_MAX_AGE_MS = DEDUP_COOKIE_MAX_AGE_SECONDS * 1000;
// Browsers cap a single cookie at ~4KB (name+value). Each entry serializes to
// ~60-70 bytes once JSON+URI-encoded, so 300 entries (~19-21KB) would never
// actually fit - the write silently fails past ~60 entries and new keys stop
// persisting. 50 keeps the cookie safely under the limit so pruning's own
// keep-most-recent eviction is what trims state, not a silent browser drop.
const DEDUP_MAX_ENTRIES = 50;
const CMS_FETCH_TIMEOUT_MS = 10000;

type DedupStateMap = Record<string, number>;

function getDedupCookieDomainAttr(): string {
  try {
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    return host.endsWith('unacademy.com')
      ? `; domain=${DEDUP_COOKIE_DOMAIN}`
      : '';
  } catch (e) {
    return '';
  }
}

function getDedupCookieSecureAttr(): string {
  try {
    return typeof window !== 'undefined' &&
      window.location.protocol === 'https:'
      ? '; Secure'
      : '';
  } catch (e) {
    return '';
  }
}

function readDedupState(): DedupStateMap {
  try {
    if (typeof document === 'undefined') return {};
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${DEDUP_COOKIE_NAME}=([^;]*)`)
    );
    if (!match) return {};
    const parsed = JSON.parse(decodeURIComponent(match[1]));
    const state = parsed && typeof parsed === 'object' ? parsed : {};
    return pruneExpiredByWindow(state);
  } catch (e) {
    return {};
  }
}

// An entry past its own event's CMS-configured window no longer blocks a
// resend anyway (checkAndMarkSendToCrm would already treat it as expired) -
// so it's dead weight taking up cookie space until the fixed-age/count prune
// in writeDedupState catches it, possibly up to 30 days later. Dropping it
// here, on every read, keeps the cookie lean without changing any dedup
// decision.
function pruneExpiredByWindow(state: DedupStateMap): DedupStateMap {
  const now = Date.now();
  const entries = Object.entries(state).filter(
    ([key, lastSentAt]) =>
      typeof lastSentAt === 'number' &&
      now - lastSentAt < getWindowMsForKey(key)
  );
  return Object.fromEntries(entries);
}

function getWindowMsForKey(key: string): number {
  const eventName = Object.values(CMS_EVENT_FIELD_NAMES).find(
    (name) => key === name || key.startsWith(`${name}_`)
  );
  return eventName ? getWindowMs(eventName) : DEFAULT_WINDOW_MS;
}

function pruneDedupState(state: DedupStateMap): DedupStateMap {
  const now = Date.now();
  let entries = Object.entries(state).filter(
    ([, lastSentAt]) =>
      typeof lastSentAt === 'number' &&
      now - lastSentAt < DEDUP_ENTRY_MAX_AGE_MS
  );
  if (entries.length > DEDUP_MAX_ENTRIES) {
    entries = entries.sort((a, b) => b[1] - a[1]).slice(0, DEDUP_MAX_ENTRIES);
  }
  return Object.fromEntries(entries);
}

function writeDedupState(state: DedupStateMap): void {
  try {
    if (typeof document === 'undefined') return;
    const pruned = pruneDedupState(state);
    document.cookie =
      `${DEDUP_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(pruned))}` +
      `; path=/; max-age=${DEDUP_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax` +
      `${getDedupCookieDomainAttr()}${getDedupCookieSecureAttr()}`;
  } catch (e) {
    // no-op;
  }
}

export function clearCmsEventDedupState(): void {
  try {
    if (typeof document === 'undefined') return;
    document.cookie =
      `${DEDUP_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax` +
      `${getDedupCookieDomainAttr()}${getDedupCookieSecureAttr()}`;
  } catch (e) {
    // no-op
  }
}

const DEFAULT_WINDOW_MS = 24 * 60 * 60 * 1000;

const CMS_EVENT_FIELD_NAMES: Record<string, string> = {
  ClxLessonView: 'CLX - Lesson View',
  ClxSuccessfulView_5Mins: 'CLX - Successful View 5 Mins',
  CourseViewed: 'Course Viewed',
  SelfStudy_UpsellCarouselViewed: 'Self Study - Upsell Carousel Viewed',
  Batch_BatchViewed: 'Batch - Batch Viewed',
  EducatorProfileViewed: 'Educator Profile Viewed'
};

async function fetchEventDedupConfig(): Promise<Record<string, number>> {
  const controller =
    typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), CMS_FETCH_TIMEOUT_MS)
    : null;
  try {
    const response = await fetch(AMPLITUDE_EVENT_CONFIG_URL, {
      signal: controller?.signal
    });
    if (!response.ok) return {};
    return await response.json();
  } catch (e) {
    // Includes a timed-out/aborted fetch — falls back to {} (-> every
    // event's window resolves to DEFAULT_WINDOW_MS) rather than leaving
    // initEventDedupConfig's cached promise unresolved forever, which would
    // otherwise stall every getEventMeta call on this tab indefinitely.
    return {};
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

function readCachedRawConfig(): Record<string, number> | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(CONFIG_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed &&
      typeof parsed === 'object' &&
      Object.keys(parsed).length > 0
      ? parsed
      : null;
  } catch (e) {
    return null;
  }
}

function writeCachedRawConfig(rawConfig: Record<string, number>): void {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    window.sessionStorage.setItem(
      CONFIG_STORAGE_KEY,
      JSON.stringify(rawConfig)
    );
  } catch (e) {
    // no-op
  }
}

let windowMsCache: Record<string, number> | null = null;
let configFetchPromise: Promise<void> | null = null;
// Set true once initEventDedupConfig's promise has settled — success,
// failure, or timeout, it doesn't matter, just that it's no longer pending.
// Lets a caller check synchronously whether awaiting getEventMeta would be
// free (config already resolved) before deciding whether to await it ahead
// of a time-sensitive action like navigation.
let isConfigSettled = false;

export function isCmsEventDedupConfigReady(): boolean {
  return isConfigSettled;
}

function parseAndCache(rawConfig: Record<string, number>): void {
  const parsed: Record<string, number> = {};
  Object.keys(rawConfig).forEach((field) => {
    const eventName = CMS_EVENT_FIELD_NAMES[field];
    const millis = rawConfig[field];
    if (
      eventName != null &&
      typeof millis === 'number' &&
      !Number.isNaN(millis)
    ) {
      parsed[eventName] = millis;
    }
  });
  windowMsCache = parsed;
}

export function initEventDedupConfig(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (configFetchPromise) {
    return configFetchPromise;
  }

  try {
    const cached = readCachedRawConfig();
    if (cached) {
      parseAndCache(cached);
      isConfigSettled = true;
      configFetchPromise = Promise.resolve();
      return configFetchPromise;
    }
  } catch (e) {
    // no-op;
  }

  configFetchPromise = fetchEventDedupConfig()
    .then((rawConfig) => {
      parseAndCache(rawConfig);
      writeCachedRawConfig(rawConfig);
    })
    .catch(() => {
      // no-op;
    })
    .finally(() => {
      isConfigSettled = true;
    });

  return configFetchPromise;
}

function getWindowMs(eventName: string): number {
  if (windowMsCache && typeof windowMsCache[eventName] === 'number') {
    return windowMsCache[eventName];
  }
  return DEFAULT_WINDOW_MS;
}

function buildUniquenessKey(
  eventName: string,
  keyParts: Record<string, string | undefined>
): string {
  const segments = [eventName];
  Object.keys(keyParts).forEach((partKey) => {
    const value = keyParts[partKey];
    if (value !== undefined && value !== null && value !== '') {
      segments.push(String(value));
    }
  });
  return segments.join('_');
}

function checkAndMarkSendToCrm(
  uniquenessKey: string,
  windowMs: number
): boolean {
  try {
    const state = readDedupState();
    const now = Date.now();
    const lastSent = state[uniquenessKey];

    if (typeof lastSent === 'number' && now - lastSent < windowMs) {
      return false;
    }

    state[uniquenessKey] = now;
    writeDedupState(state);
    return true;
  } catch (e) {
    return true;
  }
}

export async function getEventMeta(
  eventName: string,
  keyParts: Record<string, string | undefined> = {}
): Promise<{ send_to_crm: boolean }> {
  try {
    if (!keyParts.goal_uid) {
      return { send_to_crm: true };
    }
    await initEventDedupConfig();
    const uniquenessKey = buildUniquenessKey(eventName, keyParts);
    const windowMs = getWindowMs(eventName);
    const sendToCrm = checkAndMarkSendToCrm(uniquenessKey, windowMs);

    return { send_to_crm: sendToCrm };
  } catch (e) {
    return { send_to_crm: true };
  }
}

export default getEventMeta;
